import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";

/**
 * Barra superior de "te quedaste sin créditos" — ADR-2026-07-30-barra-superior-creditos-home-ancho.
 *
 * Franja finita arriba de todo del **panel de contenido**, por encima del saludo
 * ("Buenos días, …"). Ocupa el 100% de ese panel, no del viewport: va montada
 * dentro de la página, así que la sidebar queda afuera sola —sin `fixed` ni
 * compensar `sidebarWidth`, que es lo que complica a las otras barras de la app
 * (ver `HomeTemplatesSection`)—.
 *
 * **Sólo desktop.** En mobile no hay sidebar (el "100% del contenedor derecho" no
 * significa nada), el saludo no existe —manda el hero de modos— y el aviso ya lo
 * da el banner de tarjeta de `HomeBanners`. En `<sm` esto no renderiza nada, así
 * que tampoco pelea con el margen negativo con el que el hero cancela el padding
 * superior del `<main>`.
 *
 * El `%` sale del cupón real (`discountPercent`, ADR-2026-07-30-banner-home-habla-descuento-real): sin cupón vigente no se
 * promete descuento y el CTA baja a "Ver planes".
 */
export interface CreditsTopBarProps {
  /**
   * Qué se avisa: `sin` (saldo en cero) o `pocos` (por debajo del 5% de la
   * capacidad del plan). El criterio es único para todos los planes y vive en
   * `lib/creditsStatus` (ADR-2026-07-30-solo-criterio-saldo-toda-app).
   */
  estado: 'sin' | 'pocos';
  /** Saldo restante. Sólo se muestra en `pocos`, donde el número es el argumento. */
  restantes?: number;
  /** Descuento vigente en % entero, o `null` si no hay cupón activo. */
  discountPercent: number | null;
  /** Cierra la barra (el contenedor decide si lo recuerda). */
  onDismiss: () => void;
  /**
   * Qué hace el CTA. Sin esto navega a `/subscription`, que es el comportamiento
   * original. El shell de `/inicio` lo pasa para abrir el catálogo como overlay y
   * no sacar al usuario de la pantalla (ADR-2026-07-31-mejorar-plan-desde-abre-catalogo).
   */
  onUpgrade?: () => void;
  /**
   * Tratamiento de color:
   * - `soft` (default) — tinte lavanda con el CTA azul. Es el de la home, donde
   *   el panel es claro y la barra no tiene que gritar.
   * - `brand` — fondo azul de marca con el CTA blanco, para shells que ya son
   *   claros y planos y donde el tinte suave se pierde (`/inicio`).
   */
  variant?: "soft" | "brand";
  /**
   * Layout del contenedor: márgenes y radio. Lo pone el consumidor porque
   * depende del shell — a ras en la home (el panel de contenido llega al borde) y
   * con el margen y el radio de la sidebar flotante en `/inicio`.
   */
  className?: string;
}

export function CreditsTopBar({
  estado,
  restantes,
  discountPercent,
  onDismiss,
  onUpgrade,
  variant = "soft",
  className = "",
}: CreditsTopBarProps) {
  const navigate = useNavigate();
  const off = discountPercent !== null ? `${discountPercent}% OFF` : null;
  const brand = variant === "brand";

  // "Mejorá tu plan" y no "Suscribite": el aviso ahora sale para cualquier plan
  // (ADR-2026-07-30-solo-criterio-saldo-toda-app), y a alguien que ya paga no se le pide que se suscriba.
  const gancho =
    estado === "sin"
      ? "Te quedaste sin créditos."
      : restantes === 1
        ? "Te queda 1 crédito."
        : `Te quedan ${restantes} créditos.`;
  const remate = estado === "sin" ? "y seguí creando." : "antes de frenar.";

  return (
    // Sin `w-full`: es un bloque, ya ocupa el ancho disponible, y con `width:100%`
    // cualquier margen lateral que le pase el consumidor lo haría desbordar.
    <div
      className={`hidden sm:block ${
        brand ? "bg-brand-primary" : "bg-[#EEF1FF] border-b border-[#D9E0FF]"
      } ${className}`}
    >
      {/* `px-12` a los dos lados: la cruz va absoluta a la derecha, y reservando
          su lugar de un solo lado el contenido centrado quedaría corrido. */}
      <div className="relative flex items-center justify-center gap-3 px-12 py-2.5">
        {/* Corto a propósito: en un panel de ~760px (viewport 1024 con la sidebar
            abierta) una línea más larga se parte en dos y la barra deja de
            leerse como una franja. */}
        <p
          className={`text-[14px] leading-[20px] ${
            brand ? "text-white/90" : "text-brand-black/90"
          }`}
        >
          <span className="font-bold">{gancho}</span>{" "}
          {off ? (
            <>
              Mejorá tu plan con <span className="font-bold">{off}</span> {remate}
            </>
          ) : (
            <>Mejorá tu plan {remate}</>
          )}
        </p>
        <button
          type="button"
          onClick={() => (onUpgrade ? onUpgrade() : navigate("/subscription"))}
          className={`inline-flex h-8 shrink-0 items-center justify-center rounded-full px-4 text-[13px] font-semibold shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
            brand
              ? "bg-white text-brand-primary hover:bg-white/90 focus:ring-white focus:ring-offset-brand-primary"
              : "bg-brand-primary text-white hover:bg-brand-primary-hover focus:ring-brand-primary"
          }`}
        >
          {off ? `Aprovechar ${off}` : "Ver planes"}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Cerrar aviso"
          className={`absolute right-4 top-1/2 -translate-y-1/2 rounded-full p-1 transition-colors ${
            brand
              ? "text-white/60 hover:bg-white/15 hover:text-white"
              : "text-brand-black/40 hover:bg-black/[0.06] hover:text-brand-black/70"
          }`}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default CreditsTopBar;
